import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

const noopRegistration = () => () => {};
const PageLoadingContext = createContext({
  loading: false,
  hasNav: false,
  registerLoading: noopRegistration,
  registerNav: noopRegistration,
});

export function PageLoadingProvider({ children }: PropsWithChildren) {
  const [pending, setPending] = useState(0);
  const [navs, setNavs] = useState(0);
  const registerLoading = useCallback(() => {
    setPending((count) => count + 1);
    return () => setPending((count) => count - 1);
  }, []);
  const registerNav = useCallback(() => {
    setNavs((count) => count + 1);
    return () => setNavs((count) => count - 1);
  }, []);
  const value = useMemo(
    () => ({
      loading: pending > 0,
      hasNav: navs > 0,
      registerLoading,
      registerNav,
    }),
    [pending, navs, registerLoading, registerNav],
  );
  return (
    <PageLoadingContext.Provider value={value}>
      {children}
    </PageLoadingContext.Provider>
  );
}

export function usePageLoading(active: boolean) {
  const { registerLoading } = useContext(PageLoadingContext);
  useEffect(() => {
    if (active) {
      return registerLoading();
    }
  }, [active, registerLoading]);
}

export function usePageLoadingState() {
  return useContext(PageLoadingContext);
}
